import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, FileText, MapPin, Heart, Music, AlertTriangle, Scale } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const Terms = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="p-6 max-w-4xl mx-auto">
        <Button 
          variant="ghost" 
          onClick={() => navigate(-1)}
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Voltar
        </Button>

        <div className="flex items-center gap-3 mb-6">
          <FileText className="w-8 h-8 text-primary" />
          <h1 className="text-3xl font-bold">Termos de Uso</h1>
        </div>
        
        <div className="space-y-6 text-muted-foreground">
          <section>
            <h2 className="text-xl font-semibold text-foreground mb-3">1. Aceitação dos Termos</h2>
            <p>
              Ao acessar e usar o aplicativo You Better Run ("YBR"), você concorda em cumprir estes Termos de Uso. 
              Se você não concordar com qualquer parte destes termos, não poderá usar nosso serviço.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mb-3">2. Descrição do Serviço</h2>
            <p>
              O YBR é uma plataforma de corrida que oferece:
            </p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Rastreamento de corridas via GPS</li>
              <li>Monitoramento de frequência cardíaca (BPM)</li>
              <li>Integração musical com Spotify</li>
              <li>Sistema de mentoria</li>
              <li>Comunidade local de corredores</li>
              <li>Recursos de gamificação e progressão</li>
              <li>Funcionalidade SOS de segurança</li>
            </ul>
          </section>

          {/* Geolocalização */}
          <Card className="border-orange-500/30">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <MapPin className="w-5 h-5 text-orange-500" />
                3. Uso de Geolocalização (GPS)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p>
                O YBR utiliza dados de localização GPS para funcionalidades essenciais:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Rastreamento preciso de rotas durante corridas</li>
                <li>Cálculo de distância e pace em tempo real</li>
                <li>Funcionalidade SOS para emergências</li>
                <li>Descoberta de corredores e eventos próximos</li>
                <li>Recomendações musicais baseadas em localização</li>
              </ul>
              <div className="bg-orange-500/10 p-3 rounded-lg mt-3">
                <p className="text-sm">
                  <strong>Consentimento:</strong> Ao usar o YBR, você consente com a coleta de dados de 
                  localização durante corridas. Você pode gerenciar permissões nas configurações do dispositivo.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Dados de Saúde */}
          <Card className="border-red-500/30">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Heart className="w-5 h-5 text-red-500" />
                4. Dados de Saúde (HealthKit / Google Fit)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p>
                Com sua permissão explícita, o YBR pode acessar dados de saúde:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li><strong>Apple HealthKit (iOS):</strong> Frequência cardíaca, passos, calorias</li>
                <li><strong>Google Fit (Android):</strong> Dados de atividades físicas e BPM</li>
                <li><strong>Dispositivos BLE:</strong> Monitores cardíacos compatíveis</li>
              </ul>
              <div className="bg-red-500/10 p-3 rounded-lg mt-3">
                <p className="text-sm">
                  <strong>Importante:</strong> Dados de saúde são usados exclusivamente para melhorar 
                  sua experiência de treino. Você pode revogar acesso a qualquer momento nas Configurações.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Integração Spotify */}
          <Card className="border-green-500/30">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Music className="w-5 h-5 text-green-500" />
                5. Integração com Spotify
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p>
                A integração com Spotify permite recursos musicais avançados:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Sincronização de BPM musical com ritmo de corrida</li>
                <li>Playlists adaptativas baseadas na intensidade do treino</li>
                <li>Recomendações por localização (praia, parque, urbano)</li>
                <li>Controle de reprodução durante corridas</li>
              </ul>
              <div className="bg-green-500/10 p-3 rounded-lg mt-3">
                <p className="text-sm">
                  <strong>Termos do Spotify:</strong> Ao conectar sua conta, você também concorda com os{' '}
                  <a href="https://www.spotify.com/legal/end-user-agreement/" target="_blank" rel="noopener noreferrer" className="text-green-600 dark:text-green-400 hover:underline">
                    Termos de Uso do Spotify
                  </a>. Você pode desconectar sua conta a qualquer momento.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Saúde e Segurança */}
          <Card className="border-yellow-500/30">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <AlertTriangle className="w-5 h-5 text-yellow-500" />
                6. Saúde e Segurança
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="font-semibold">
                IMPORTANTE: O YBR não substitui orientação médica profissional.
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Consulte um médico antes de iniciar qualquer programa de exercícios</li>
                <li>A funcionalidade SOS é complementar, não substitui serviços de emergência (192/193)</li>
                <li>Você é responsável por sua própria segurança durante as corridas</li>
                <li>Métricas de saúde são estimativas e não devem ser usadas para diagnóstico</li>
                <li>Respeite seus limites físicos e condições climáticas</li>
              </ul>
              <div className="bg-yellow-500/10 p-3 rounded-lg mt-3">
                <p className="text-sm font-medium">
                  ⚠️ Em caso de mal-estar, tontura ou dor durante exercícios, pare imediatamente 
                  e procure atendimento médico.
                </p>
              </div>
            </CardContent>
          </Card>

          <section>
            <h2 className="text-xl font-semibold text-foreground mb-3">7. Responsabilidades do Usuário</h2>
            <p>Você concorda em:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Fornecer informações precisas e atualizadas</li>
              <li>Manter a confidencialidade de sua conta</li>
              <li>Usar o serviço de forma responsável e legal</li>
              <li>Respeitar outros usuários e mentores</li>
              <li>Não compartilhar conteúdo ofensivo ou inadequado</li>
              <li>Não usar o app enquanto dirige ou em situações de risco</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mb-3">8. Privacidade de Dados</h2>
            <p>
              Coletamos e processamos seus dados conforme nossa{' '}
              <a href="/privacy" className="text-primary hover:underline">Política de Privacidade</a>.
              Isso inclui dados de localização, métricas de saúde, interações com Spotify e dados sociais.
              Todos os dados são tratados em conformidade com a LGPD.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mb-3">9. Propriedade Intelectual</h2>
            <p>
              Todo o conteúdo do YBR (design, código, textos, logos) é propriedade da You Better Run e está 
              protegido por leis de direitos autorais. Você não pode copiar, modificar ou distribuir nosso conteúdo 
              sem autorização.
            </p>
          </section>

          {/* Limitação de Responsabilidade */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Scale className="w-5 h-5 text-primary" />
                10. Limitação de Responsabilidade
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="mb-3">O YBR não se responsabiliza por:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Lesões físicas decorrentes da prática de corrida</li>
                <li>Imprecisões em métricas de GPS, BPM ou calorias</li>
                <li>Falhas em integrações de terceiros (Spotify, HealthKit, Google Fit)</li>
                <li>Interrupções de serviço ou perda de dados</li>
                <li>Ações de outros usuários ou terceiros</li>
                <li>Uso indevido da funcionalidade SOS</li>
              </ul>
            </CardContent>
          </Card>

          <section>
            <h2 className="text-xl font-semibold text-foreground mb-3">11. Modificações dos Termos</h2>
            <p>
              Reservamos o direito de modificar estes termos a qualquer momento. Usuários serão notificados 
              sobre mudanças significativas por email ou notificação no app. O uso continuado após mudanças 
              constitui aceitação dos novos termos.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mb-3">12. Rescisão</h2>
            <p>
              Podemos suspender ou encerrar sua conta se você violar estes termos. Você pode encerrar sua conta 
              a qualquer momento através das configurações do aplicativo, com direito à exportação e exclusão 
              de seus dados conforme a LGPD.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mb-3">13. Lei Aplicável</h2>
            <p>
              Estes termos são regidos pelas leis brasileiras. Qualquer disputa será resolvida nos tribunais 
              competentes do Brasil, com preferência para métodos alternativos de resolução de conflitos.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mb-3">14. Contato</h2>
            <p>
              Para dúvidas sobre estes termos:
            </p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Email: <a href="mailto:suporte@youbetterrun.app" className="text-primary hover:underline">suporte@youbetterrun.app</a></li>
              <li>Privacidade: <a href="mailto:privacidade@youbetterrun.app" className="text-primary hover:underline">privacidade@youbetterrun.app</a></li>
            </ul>
          </section>

          <div className="pt-6 border-t text-sm">
            <p>Última atualização: {new Date().toLocaleDateString('pt-BR')}</p>
            <p className="mt-2">You Better Run - Todos os direitos reservados</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Terms;
